import type Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, labelStyle } from '../theme';
import { createButton } from './button';
import { createListRow } from './panel';
import { maybeShowTutorial } from './adviserPanel';
import { plateKey, INCIDENT_PLATE, VESSEL_PLATE } from '../assets';
import { incidentType, type Incident } from '@core/mission/incident';
import type { Campaign } from '@core/campaign/campaign';
import type { Route } from '@core/map/navigation';
import { Voyage } from '@core/voyage/voyage';
import {
  RESEARCH_SHIP,
  SUBMARINE,
  SPEED_SETTINGS,
  type SpeedSetting,
  type VesselProfile,
} from '@core/voyage/profiles';
import { seaStateKey } from '@core/voyage/weather';
import { audio } from '../audio/engine';

/**
 * Einsatzplanung als Tafel ueber der Karte.
 *
 * Bis M8 war das ein eigener Bildschirm. Das kostete den entscheidenden
 * Zusammenhang: Man waehlte Fahrzeug und Fahrtstufe, ohne zu sehen, wohin der
 * Kurs eigentlich fuehrt. Der Umweg um die Baenke stand als Prozentzahl da.
 *
 * Jetzt bleibt die Karte sichtbar, und jede Aenderung zeichnet den Kurs neu —
 * `onCourse` reicht ihn an die Kartenszene zurueck. Man *sieht* den Umweg,
 * statt ihn zu lesen, und das ist derselbe Unterschied wie zwischen einer
 * Wettervorhersage und einem Blick aus dem Fenster.
 */

export interface BriefingOptions {
  readonly campaign: Campaign;
  readonly incident: Incident;
  /** Laeuft bei jedem Neuaufbau mit dem aktuell gerechneten Kurs. */
  readonly onCourse: (route: Route) => void;
  readonly onDepart: (order: {
    incident: Incident;
    vesselId: 'ship' | 'submarine';
    speed: SpeedSetting;
  }) => void;
  readonly onClose: () => void;
}

const PANEL_WIDTH = 640;

export function showBriefing(scene: Phaser.Scene, options: BriefingOptions): void {
  let vessel: VesselProfile = RESEARCH_SHIP;
  let speed: SpeedSetting = 'cruise';
  let container: Phaser.GameObjects.Container | null = null;
  let shade: Phaser.GameObjects.Rectangle | null = null;

  const close = (): void => {
    container?.destroy();
    shade?.destroy();
    container = null;
    shade = null;
  };

  const build = (): void => {
    close();

    const { width, height } = scene.scale;
    const panelWidth = Math.min(PANEL_WIDTH, width - 48);

    // Die Karte darunter leicht abdunkeln — sie soll sichtbar bleiben, dort
    // liegt schliesslich der Kurs, um den es geht. Der Schleier faengt
    // zugleich Klicks ab, die sonst auf einem Marker dahinter landeten.
    shade = scene.add
      .rectangle(0, 0, width, height, PALETTE.abyss, 0.35)
      .setOrigin(0, 0)
      .setDepth(1500)
      .setInteractive();

    const parts: Phaser.GameObjects.GameObject[] = [];
    const voyage = buildVoyage();
    const estimates = voyage.compareSettings();
    options.onCourse(voyage.route);

    const frame = scene.add.rectangle(0, 0, panelWidth, 0, PALETTE.panel, 0.97).setOrigin(0, 0);
    frame.setStrokeStyle(1, PALETTE.panelEdge);
    parts.push(frame);

    // Kopf: Bild des verunglueckten Fahrzeugs und die harten Zahlen.
    const plateHeight = 84;
    const plate = plateKey(INCIDENT_PLATE[options.incident.kind]);
    const hasPlate = scene.textures.exists(plate);
    if (hasPlate) {
      parts.push(
        scene.add
          .image(14, 14, plate)
          .setOrigin(0, 0)
          .setDisplaySize(plateHeight * 1.5, plateHeight),
      );
    }

    const headLeft = hasPlate ? 14 + plateHeight * 1.5 + 14 : 14;

    // Die Kopfzeilen setzen aufeinander auf, statt auf festen Zeilen zu
    // stehen: Mit vergroesserter Schrift liefen sie sonst ineinander.
    let headY = 14;
    const headLine = (text: string, style: object): void => {
      const object = scene.add.text(headLeft, headY, text, style);
      parts.push(object);
      headY += object.height + 4;
    };

    headLine(t('briefing.title'), titleStyle(16));
    headLine(
      `${t(incidentType(options.incident.kind).nameKey)} · ${options.incident.vesselName}`,
      readoutStyle(12),
    );
    headLine(
      `${t('call.age', { hours: Math.round(options.campaign.ageOf(options.incident)) })}  ·  ${t(
        'call.souls',
        { count: options.incident.souls },
      )}  ·  ${t('call.depth', { metres: Math.round(options.incident.depthMetres) })}`,
      readoutStyle(11, true),
    );
    headLine(departureLine(), {
      ...readoutStyle(11),
      color: options.campaign.isAtBase(vessel.id) ? PALETTE.inkFaint : TEXT_COLOUR.landmark,
    });

    let y = Math.max(14 + plateHeight, headY) + 16;

    // Fahrzeugwahl
    const vesselLabel = scene.add.text(14, y, t('briefing.vessel'), labelStyle());
    parts.push(vesselLabel);
    y += vesselLabel.height + 6;

    let vesselRowHeight = 0;
    [RESEARCH_SHIP, SUBMARINE].forEach((profile, index) => {
      const tooDeep = options.incident.depthMetres > profile.maxWorkingDepthMetres;
      const detail = tooDeep
        ? `${profile.knots.cruise} kn · ${t('briefing.depth.short')}`
        : `${profile.knots.cruise} kn · ${t('briefing.distance', {
            miles: Math.round(options.campaign.distanceTo(options.incident, profile.id)),
          })}`;

      const columnLeft = 14 + index * ((panelWidth - 28) / 2);
      const columnWidth = (panelWidth - 28) / 2 - 8;
      const thumb = 32;
      const vesselPlate = plateKey(VESSEL_PLATE[profile.id]);
      const hasThumb = scene.textures.exists(vesselPlate);

      if (hasThumb) {
        parts.push(
          scene.add
            .image(columnLeft, y, vesselPlate)
            .setOrigin(0, 0)
            .setDisplaySize(thumb * 1.5, thumb)
            .setAlpha(vessel.id === profile.id ? 1 : 0.45),
        );
      }

      const rowLeft = hasThumb ? columnLeft + thumb * 1.5 + 6 : columnLeft;
      const row = createListRow(scene, rowLeft, y, {
        width: columnWidth - (rowLeft - columnLeft),
        height: 38,
        title: t(profile.nameKey),
        detail,
        selected: vessel.id === profile.id,
        onClick: () => {
          vessel = profile;
          build();
        },
      });
      parts.push(row);
      vesselRowHeight = Math.max(vesselRowHeight, row.height);
    });

    y += vesselRowHeight + 14;

    // Fahrtstufen mit Vorausberechnung
    const speedLabel = scene.add.text(14, y, t('briefing.speed'), labelStyle());
    parts.push(speedLabel);
    y += speedLabel.height + 6;

    SPEED_SETTINGS.forEach((setting) => {
      const estimate = estimates[setting];
      const forecast = options.campaign.forecastOnArrival(
        options.incident,
        options.campaign.clockHours + estimate.hours,
        vessel.id,
      );

      const detail = [
        t('briefing.eta', { hours: Math.round(estimate.hours) }),
        t('briefing.fuel', {
          used: Math.round(estimate.fuelUsed),
          available: Math.round(options.campaign.vessels[vessel.id].fuel),
        }),
        estimate.hullLoss > 0.5
          ? t('briefing.hull', { value: Math.round(estimate.hullLoss) })
          : t('briefing.sea', { sea: t(seaStateKey(estimate.worstSeaState)) }),
      ].join('  ·  ');

      const row = createListRow(scene, 14, y, {
        width: panelWidth - 28,
        height: 42,
          title: t(`briefing.speed.${setting}`),
          detail,
          badge:
            forecast.alternative === null
              ? t(`outcome.${forecast.tier}`)
              : t('briefing.forecast.uncertain', {
                  tier: t(`outcome.${forecast.tier}`),
                  alternative: t(`outcome.${forecast.alternative}`),
                }),
          badgeColor:
            forecast.tier === 'rescue'
              ? '#8fd9c4'
              : forecast.tier === 'nothing'
                ? '#c9736f'
                : '#e0b070',
        selected: speed === setting,
        onClick: () => {
          speed = setting;
          build();
        },
      });
      parts.push(row);

      y += row.height + 6;
    });

    /*
     * Was ein Kurswechsel aufgibt.
     *
     * Seit beide Fahrzeuge gleichzeitig fahren koennen, ist ein Klick auf
     * einen Notruf-Marker waehrend einer laufenden Fahrt kein Versehen mehr,
     * sondern eine Moeglichkeit — man kann umdisponieren. Aber die Stunden,
     * die schon in die alte Fahrt geflossen sind, sind weg, und das muss
     * dastehen, bevor man es tut.
     */
    const abandoning = options.campaign.abandonmentCost(vessel.id);
    if (abandoning !== null) {
      const what =
        abandoning.incident === null
          ? t('briefing.abandon.homebound')
          : `${t(incidentType(abandoning.incident.kind).nameKey)} · ${abandoning.incident.vesselName}`;

      parts.push(
        scene.add.text(
          14,
          y,
          t('briefing.abandon', { what, hours: Math.round(abandoning.spentHours) }),
          { ...readoutStyle(11), color: '#c9736f', wordWrap: { width: panelWidth - 28 } },
        ),
      );
      y += 22;
    }

    // Warnungen
    const chosen = estimates[speed];
    if (voyage.route.detourFactor > 1.05) {
      parts.push(
        scene.add.text(
          14,
          y,
          t('briefing.detour', {
            percent: Math.round((voyage.route.detourFactor - 1) * 100),
            miles: Math.round(voyage.route.lengthNm),
          }),
          { ...readoutStyle(11), color: '#e0b070' },
        ),
      );
      y += 18;
    }
    if (!chosen.fuelSufficient) {
      parts.push(
        scene.add.text(14, y, t('briefing.fuel.short'), { ...readoutStyle(11), color: '#c9736f' }),
      );
      y += 18;
    }
    if (options.incident.depthMetres > vessel.maxWorkingDepthMetres) {
      parts.push(
        scene.add.text(14, y, t('briefing.depth.warning'), {
          ...readoutStyle(11),
          color: '#e0b070',
        }),
      );
      y += 18;
    }

    y += 10;
    parts.push(
      createButton(scene, 14 + 100, y + 16, t('briefing.cancel'), () => {
        close();
        options.onClose();
      }, { width: 180, height: 34, fontSize: 12 }),
      createButton(scene, panelWidth - 14 - 100, y + 16, t(abandoning === null ? 'briefing.depart' : 'briefing.redirect'), () => {
        close();
        options.onDepart({ incident: options.incident, vesselId: vessel.id, speed });
      }, { width: 180, height: 34, fontSize: 12 }),
    );

    const panelHeight = y + 40;
    frame.height = panelHeight;

    container = scene.add.container((width - panelWidth) / 2, height - panelHeight - 16, parts);
    container.setDepth(1501);

    maybeShowTutorial(scene, options.campaign, 'briefing');
  };

  function departureLine(): string {
    if (options.campaign.isAtBase(vessel.id)) return t('briefing.from.base');
    return t('briefing.from.sea', {
      miles: Math.round(options.campaign.distanceHome(vessel.id)),
    });
  }

  function buildVoyage(): Voyage {
    const condition = options.campaign.vessels[vessel.id];
    const start = options.campaign.positionOf(vessel.id);

    return new Voyage({
      route: options.campaign.routeTo(options.incident, vessel.draughtMetres, start),
      profile: vessel,
      from: start,
      to: options.incident.position,
      weather: options.campaign.weather,
      startHours: options.campaign.clockHours,
      fuel: condition.fuel,
      hull: condition.hull,
      speed,
    });
  }

  audio.resume();
  build();
}
